// View-model types for the treasury deposit-requests (recharge) module.
// Produced by ../api/depositRequestsParser.ts. Consumers (containers,
// components, composables) bind to these — never to the wire DTOs in
// ../api/depositRequestsContracts.ts.

export enum DEPOSIT_REQUEST_STATUS {
  INITIATED = 'initiated',
  PENDING_VERIFICATION = 'pending_verification',
  COMPLETED = 'completed',
  REJECTED = 'rejected',
  EXPIRED = 'expired',
  CANCELED = 'canceled',
}

export type DepositRequestStatus = `${DEPOSIT_REQUEST_STATUS}`

// ─── Row (list response item) ────────────────────────────────────────────────

export interface DepositRequestRow {
  id: string
  investorId: string
  fiatWalletId: string
  status: DepositRequestStatus
  amount: string
  currencyCode: string
  paymentReference: string
  utrNumber: string | null
  transactionDocument: string | null
  approvedBankReference: string | null
  rejectionReason: string | null
  completedAt: string | null
  createdAt: string
  investorName: string
  investorEmail: string
}

export interface DepositRequestsListResponse {
  data: DepositRequestRow[]
  meta: {
    total: number
    page: number
    limit: number
    totalPages: number
  }
}

// ─── Status counts ───────────────────────────────────────────────────────────

export interface DepositRequestStatusCounts {
  pendingVerification: number
  completed: number
  rejected: number
}

// ─── Detail ──────────────────────────────────────────────────────────────────

export interface DepositRequestInvestor {
  id: string
  firstName: string | null
  lastName: string | null
  email: string
  kycStatus: string | null
}

export interface DepositRequestProof {
  paymentReference: string
  documentUrl: string | null
  submittedAt: string
}

export interface DepositRequestTreasuryAccount {
  id: string
  accountName: string
  bankName: string
  iban: string | null
  currencyCode: string
}

export interface DepositRequestAudit {
  approvedBy: string | null
  approvedAt: string | null
  approvedBankReference: string | null
}

export interface DepositRequestDetail {
  id: string
  investorId: string
  fiatWalletId: string
  status: DepositRequestStatus
  amount: string
  currencyCode: string
  rejectionReason: string | null
  createdAt: string
  completedAt: string | null
  investor: DepositRequestInvestor
  proof: DepositRequestProof
  treasuryBankAccount: DepositRequestTreasuryAccount | null
  audit: DepositRequestAudit
}

// ─── Request params / inputs ─────────────────────────────────────────────────

export interface GetDepositRequestsParams {
  page?: number
  limit?: number
  status?: DepositRequestStatus
  fromDate?: string
  toDate?: string
  investorId?: string
  investorName?: string
  investorEmail?: string
}

export interface ApproveDepositRequestInput {
  id: string
  treasuryBankAccountId: string
  approvedBankReference: string
  idempotencyKey: string
}

export interface RejectDepositRequestInput {
  id: string
  reason: string
  idempotencyKey: string
}
